import { useEffect, useState } from "react";
import { Alert, Button, Card, Descriptions, Form, InputNumber, Popconfirm, Typography, message } from "antd";
import { ExperimentOutlined } from "@ant-design/icons";
import { PageHeader } from "../../components/PageHeader";
import { useUser } from "../../context/UserContext";
import { useApi } from "../../helpers/useApi";
import { hasPermission } from "../../helpers/permissions";
import type { DeveloperEnvironment, FakeDataRequest, FakeDataSummary } from "../../api-models";

// The counts the generator accepts, in dependency order: sites need a project, staff need a
// site, attendance needs staff assigned to one, stock needs materials and a location. The
// server fills any missing dependency itself and says so in the run's notes.
const TARGETS: { name: keyof FakeDataRequest; label: string; help: string }[] = [
  { name: "projects", label: "Projects", help: "Active projects with a start and end date." },
  { name: "sites", label: "Sites", help: "Sites under a project, each with its default store." },
  { name: "staff", label: "Staff", help: "Staff records, each posted to a site from a past date." },
  { name: "attendanceSheets", label: "Attendance sheets", help: "Draft daily sheets with a line per assigned staff member." },
  { name: "stockBalances", label: "Stock balances", help: "Opening balances posted to the stock ledger." },
];

const MAX_PER_ENTITY = 100;

const SUMMARY_ROWS: { key: keyof FakeDataSummary; label: string }[] = [
  { key: "projects", label: "Projects" },
  { key: "sites", label: "Sites" },
  { key: "staff", label: "Staff" },
  { key: "staffAssignments", label: "Staff assignments" },
  { key: "items", label: "Materials" },
  { key: "attendanceSheets", label: "Attendance sheets" },
  { key: "attendanceLines", label: "Attendance lines" },
  { key: "stockBalances", label: "Stock balances" },
];

// Developer Options is build and demo tooling rather than a business module: it is gated on
// Developer.* and hidden entirely when the "developer" module is disabled (modules.go).
export default function DeveloperOptionsPage() {
  const { user } = useUser();
  const { request, loading } = useApi();
  const [form] = Form.useForm<FakeDataRequest>();
  const [result, setResult] = useState<FakeDataSummary | null>(null);
  const [env, setEnv] = useState<DeveloperEnvironment | null>(null);

  // The server refuses to generate outside a local or development environment whoever
  // holds the permission, so ask it up front rather than letting the user fill the form in
  // and discover the guard by running into it.
  useEffect(() => {
    request<DeveloperEnvironment>({ url: "/developer/environment" }, { silent: true })
      .then(setEnv)
      .catch(() => setEnv(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allowedHere = env?.fakeDataEnabled !== false;
  const canGenerate = hasPermission(user, "Developer.GenerateData") && allowedHere;

  async function generate() {
    const values = await form.validateFields();
    const summary = await request<FakeDataSummary>({ method: "POST", url: "/developer/fake-data", data: values });
    setResult(summary);
    message.success("Sample data generated.");
  }

  return (
    <div>
      <PageHeader
        title="Developer Options"
        subtitle={env ? `Tooling for building and demoing the system — ${env.environment} environment.` : "Tooling for building and demoing the system."}
      />
      {allowedHere ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Generated data is written straight into this environment"
          description="Records are created through the same validation the normal screens use, so they behave like real data — including in reports, stock balances and the audit log. Every generated project, site, staff member and material carries a FAKE- code prefix so it can be told apart later. There is no undo."
        />
      ) : (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Sample data generation is disabled on this server"
          description={`Generated records are indistinguishable from real ones to every report and stock balance that reads them, so the generator only runs in a local or development environment. This server is running as ${env?.environment}.`}
        />
      )}

      <Card title="Generate sample data">
        <Form form={form} layout="vertical" initialValues={{ projects: 2, sites: 4, staff: 20, attendanceSheets: 10, stockBalances: 40 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            {TARGETS.map((t) => (
              <Form.Item key={t.name} name={t.name} label={t.label} extra={t.help} rules={[{ required: true, message: "Enter a number, or 0." }]}>
                <InputNumber min={0} max={MAX_PER_ENTITY} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            ))}
          </div>
          <Popconfirm
            title="Generate sample data?"
            description="This writes new records into this environment and cannot be undone."
            okText="Generate"
            onConfirm={generate}
            disabled={!canGenerate}
          >
            <Button type="primary" icon={<ExperimentOutlined />} loading={loading} disabled={!canGenerate}>
              Generate
            </Button>
          </Popconfirm>
          {!canGenerate && (
            <Typography.Text type="secondary" style={{ marginLeft: 12 }}>
              {allowedHere ? "You do not have permission to generate sample data." : `Not available in the ${env?.environment} environment.`}
            </Typography.Text>
          )}
        </Form>
      </Card>

      {result && (
        <Card title={`Last run — ${result.runId}`} style={{ marginTop: 16 }}>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }} bordered>
            {SUMMARY_ROWS.map((row) => (
              <Descriptions.Item key={row.key} label={row.label}>
                {result[row.key] as number}
              </Descriptions.Item>
            ))}
          </Descriptions>
          {result.notes.length > 0 && (
            <ul style={{ marginTop: 12, marginBottom: 0 }}>
              {result.notes.map((note) => (
                <li key={note}>
                  <Typography.Text type="secondary">{note}</Typography.Text>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
